// A connector call a provider returned as approval_required, in the thread
// (BUILD 2 C2-7): the exact arguments and tap-only Approve and Reject through
// the existing routes. Other connected writes run only when Connections allows
// them (D73), and an email send is confirmed through the chat's own action card. Without an action id the card renders `fallback`
// (the default tool row). `kind` names the route that decides it: a Pipedream
// action (/api/v1/company/connection-actions) or a custom MCP call
// (/api/v1/company/custom-mcp/actions), the voice ref a spoken confirmation or
// a withdraw names. Both are tap-only (R15).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import voiceConfirmCopy from '@/i18n/ns/voice-confirm';
import type { VoiceRef } from '@/voice/contracts';
import { voiceFocus } from '@/voice/confirm/focus';
import { useConnectionAction, type ConnectionAction } from './use-connection-action';

type ConnectionActionCardProps = {
  kind: Extract<VoiceRef['kind'], 'connection_action' | 'custom_mcp_action'>;
  toolCallId?: string;
  messageId?: string;
  output: unknown;
  fallback: ReactNode;
};
export function ConnectionActionCard(props: ConnectionActionCardProps) {
  const raw =
    props.output && typeof props.output === 'object'
      ? (props.output as Record<string, unknown>)
      : {};
  const action =
    raw.action && typeof raw.action === 'object' ? (raw.action as Record<string, unknown>) : raw;
  return (
    <ConnectionActionCardBody
      key={`${props.kind}:${String(action.id ?? '')}:${String(action.digest ?? '')}`}
      {...props}
    />
  );
}
function ConnectionActionCardBody({
  kind,
  toolCallId,
  messageId,
  output,
  fallback,
}: ConnectionActionCardProps) {
  const copy = useNamespaceCopy(voiceConfirmCopy);
  const raw = output && typeof output === 'object' ? (output as Record<string, unknown>) : {};
  const action = (
    raw.action && typeof raw.action === 'object' ? raw.action : raw
  ) as Partial<ConnectionAction>;
  const id = typeof action.id === 'string' ? action.id : typeof raw.id === 'string' ? raw.id : null;
  const title = typeof action.title === 'string' ? action.title : copy.connectedServiceAction;
  const tool = typeof action.tool === 'string' ? action.tool : copy.connectedService;
  const args =
    action.arguments && typeof action.arguments === 'object' ? action.arguments : raw.arguments;
  const outputDigest =
    typeof (action as { digest?: unknown }).digest === 'string'
      ? (action as { digest: string }).digest
      : null;
  const digest = outputDigest ?? 'connection-action';
  const endpoint = kind === 'custom_mcp_action' ? '/api/v1/company/custom-mcp/actions' : undefined;
  const { busy, error, decide } = useConnectionAction(endpoint);
  const [state, setState] = useState<
    'pending' | 'running' | 'executed' | 'rejected' | 'failed' | 'unknown'
  >(() => {
    const initial = action.state;
    return initial === 'running' ||
      initial === 'executed' ||
      initial === 'rejected' ||
      initial === 'failed' ||
      initial === 'unknown'
      ? initial
      : 'pending';
  });
  const deciding = useRef(false);
  const releaseFocus = useRef<(() => void) | null>(null);
  const ref = useMemo(
    () =>
      id
        ? {
            kind,
            id,
            digest,
            messageId: messageId ?? id,
            actionable: state === 'pending',
            shownAt: performance.now(),
          }
        : null,
    [id, kind, messageId, digest, state],
  );
  const mounted = useRef(true);
  const identity = `${kind}:${id ?? ''}:${outputDigest ?? ''}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    if (action.state && action.state !== 'pending') setState(action.state);
  }, [action.state, identity]);
  useEffect(() => {
    if (!ref || state !== 'pending') return;
    let current = true;
    const unregister = voiceFocus.register(ref);
    releaseFocus.current = unregister;
    return () => {
      current = false;
      unregister();
      if (releaseFocus.current === unregister) releaseFocus.current = null;
    };
  }, [ref, state]);
  if (!id) return fallback;
  const item: ConnectionAction = {
    id,
    connectionName:
      typeof action.connectionName === 'string' ? action.connectionName : copy.connectedService,
    tool,
    title,
    arguments: (args && typeof args === 'object' ? args : {}) as Record<string, unknown>,
    state: state === 'pending' ? 'pending' : state,
    createdAt: typeof action.createdAt === 'string' ? action.createdAt : new Date().toISOString(),
  };
  const run = async (decision: 'approve' | 'reject') => {
    if (deciding.current) return;
    const runIdentity = identityRef.current;
    const authorizedFocus = voiceFocus.current() === ref ? ref : null;
    deciding.current = true;
    setState(decision === 'approve' ? 'running' : 'rejected');
    let ok: Awaited<ReturnType<typeof decide>>;
    try {
      ok = await decide(item, decision);
    } catch {
      if (!mounted.current || identityRef.current !== runIdentity) return;
      setState('unknown');
      releaseFocus.current?.();
      releaseFocus.current = null;
      return;
    }
    try {
      if (!mounted.current || identityRef.current !== runIdentity) return;
      if (!ok.ok) {
        setState('unknown');
        releaseFocus.current?.();
        releaseFocus.current = null;
        return;
      }
      if (!mounted.current || identityRef.current !== runIdentity) return;
      setState(
        ok.action?.state === 'executed'
          ? 'executed'
          : ok.action?.state === 'failed'
            ? 'failed'
            : 'rejected',
      );
      releaseFocus.current?.();
      releaseFocus.current = null;
    } catch {
      // The server write remains authoritative when summary metadata or speech fails.
    } finally {
      releaseFocus.current?.();
      releaseFocus.current = null;
    }
  };
  return (
    <section className="ws-voice-confirm-card" data-voice-card="connection-action">
      <div className="ws-connection-action-topline">
        <div>
          <h3>{title}</h3>
          <p>
            {item.connectionName} · {tool}
          </p>
        </div>
        <span>
          {state === 'pending'
            ? copy.needsApproval
            : state === 'running'
              ? copy.running
              : state === 'executed'
                ? copy.completed
                : state === 'rejected'
                  ? copy.rejectedStatus
                  : state === 'failed'
                    ? copy.failedStatus
                    : copy.statusUnavailable}
        </span>
      </div>
      <p className="ws-muted" data-confirmation-digest={digest}>
        {copy.confirmation}
      </p>
      <dl className="ws-connection-action-details">
        <dt>{copy.actionLabel}</dt>
        <dd>{tool}</dd>
        <dt>{copy.connectionLabel}</dt>
        <dd>{item.connectionName}</dd>
        <dt>{copy.argumentsLabel}</dt>
        <dd>
          <pre>{JSON.stringify(item.arguments, null, 2)}</pre>
        </dd>
      </dl>
      <p className="ws-muted">{copy.tapOnly}</p>
      {error && <p role="alert">{error}</p>}
      {state === 'pending' && (
        <div className="ws-actions">
          <button
            type="button"
            className="ws-button primary"
            disabled={Boolean(busy)}
            onClick={() => void run('approve')}
          >
            {busy === id ? copy.working : copy.approveAndRun}
          </button>
          <button
            type="button"
            className="ws-text-button"
            disabled={Boolean(busy)}
            onClick={() => void run('reject')}
          >
            {copy.reject}
          </button>
        </div>
      )}
    </section>
  );
}
